import React, { useEffect, useState, useRef } from 'react';
import { ChevronDownIcon, MenuIcon, XIcon } from 'lucide-react';
interface HeaderProps {
  activeSection?: string;
  onNavigate?: (sectionId: string) => void;
}
export function Header({
  activeSection = 'home',
  onNavigate
}: HeaderProps) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isProductsOpen, setIsProductsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const productsRef = useRef<HTMLLIElement>(null);
  const handleNavigation = (sectionId: string) => {
    if (onNavigate) {
      onNavigate(sectionId);
    } else {
      const section = document.getElementById(sectionId);
      if (section) {
        section.scrollIntoView({
          behavior: 'smooth'
        });
      }
    }
    setIsMenuOpen(false);
    setIsProductsOpen(false);
  };
  // Handle scroll effect
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 50) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (productsRef.current && !productsRef.current.contains(event.target as Node)) {
        setIsProductsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  // Close mobile menu on resize to desktop
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 768) {
        setIsMenuOpen(false);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMenuOpen]);
  const headerClasses = isScrolled ? 'bg-white shadow-md text-navy' : 'bg-transparent text-white';
  // On non-home pages (where activeSection might not be 'home' initially or URL is different),
  // we might want to force a solid header, but the requirement says "starts transparent over hero".
  // However, if we are on a subpage like /quasar, there might not be a hero slideshow.
  // For safety in this specific task scope (redesigning App.tsx which is Home), we'll stick to scroll logic.
  // If the user navigates to a subpage, the Header component there might need adjustment,
  // but we are focused on the main App.tsx redesign.
  // To be safe for subpages, we can check window.location.pathname.
  const isHomePage = typeof window !== 'undefined' && window.location.pathname === '/';
  const finalHeaderClasses = isHomePage ? isScrolled ? 'bg-white shadow-md text-navy' : 'bg-transparent text-white' : 'bg-white shadow-md text-navy';
  const navLinkClasses = (section: string) => {
    const isActive = activeSection === section;
    if (isHomePage && !isScrolled) {
      return `block px-4 py-3 md:py-2 capitalize transition-colors rounded-lg md:rounded-none text-base ${isActive ? 'font-bold border-b-2 border-teal' : 'hover:text-teal/80'}`;
    }
    return `block px-4 py-3 md:py-2 capitalize transition-colors rounded-lg md:rounded-none text-base ${isActive ? 'text-teal font-bold' : 'text-navy hover:text-teal'}`;
  };
  return <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${finalHeaderClasses}`} data-id="element-236">
      <div className="container mx-auto max-w-[var(--maxw)] px-4 py-4 flex justify-between items-center" data-id="element-237">
        <a href="/" className="flex items-center flex-shrink-0" data-id="element-238">
          <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/cw3ea4867NqajmUxwmCcxJ/Logo_-_Company_-_HD.png" alt="COSMO IMAGE AVIATION" className={`h-12 sm:h-16 transition-all duration-300 ${isHomePage && !isScrolled ? 'brightness-0 invert' : ''}`} data-id="element-239" />
        </a>

        {/* Mobile menu button */}
        <button className={`md:hidden p-2 rounded-lg transition-colors ${isHomePage && !isScrolled ? 'text-white hover:bg-white/10' : 'text-navy hover:bg-gray-100'}`} onClick={() => setIsMenuOpen(!isMenuOpen)} aria-label="Toggle menu" data-id="element-240">
          {isMenuOpen ? <XIcon className="w-6 h-6" data-id="element-241" /> : <MenuIcon className="w-6 h-6" data-id="element-242" />}
        </button>

        {/* Mobile overlay */}
        {isMenuOpen && <div className="fixed inset-0 bg-black/50 z-40 md:hidden" onClick={() => setIsMenuOpen(false)} style={{
        top: '0'
      }} data-id="element-243" />}

        {/* Navigation */}
        <nav className={`${isMenuOpen ? 'block fixed top-0 right-0 bottom-0 w-[80%] max-w-sm bg-white z-50 overflow-y-auto shadow-2xl p-6' : 'hidden'} md:block md:static md:bg-transparent md:p-0 md:shadow-none md:overflow-visible md:w-auto`} data-id="element-244">
          {isMenuOpen && <div className="flex justify-end mb-6 md:hidden" data-id="element-245">
              <button onClick={() => setIsMenuOpen(false)} className="text-navy" data-id="element-246">
                <XIcon className="w-6 h-6" data-id="element-247" />
              </button>
            </div>}

          <ul className={`flex flex-col md:flex-row md:items-center space-y-4 md:space-y-0 md:space-x-2 lg:space-x-5 ${isMenuOpen ? 'text-navy' : ''}`} data-id="element-248">
            {['home', 'about', 'services', 'leadership', 'locations', 'contact'].map(item => <li key={item} data-id="element-249">
                <a href={`/#${item}`} className={isMenuOpen ? 'block py-2 text-lg font-medium text-navy border-b border-gray-100' : navLinkClasses(item)} onClick={e => {
              if (window.location.pathname === '/') {
                e.preventDefault();
                handleNavigation(item);
              } else {
                setIsMenuOpen(false);
              }
            }} data-id="element-250">
                  {item}
                </a>
              </li>)}

            {/* Products Dropdown */}
            <li className="relative" ref={productsRef} data-id="element-251">
              <button className={`flex items-center justify-between gap-1 w-full md:w-auto text-left text-base font-medium transition-colors ${isMenuOpen ? 'py-2 text-lg text-navy border-b border-gray-100' : isHomePage && !isScrolled ? 'text-white hover:text-teal/80' : 'text-navy hover:text-teal'}`} onClick={() => setIsProductsOpen(!isProductsOpen)} aria-expanded={isProductsOpen} aria-haspopup="true" data-id="element-252">
                Products
                <ChevronDownIcon className={`w-4 h-4 transition-transform duration-200 ${isProductsOpen ? 'rotate-180' : ''}`} data-id="element-253" />
              </button>

              {/* Dropdown Menu */}
              {isProductsOpen && <ul className="md:absolute md:top-full md:right-0 md:mt-2 md:w-56 md:bg-white md:shadow-xl md:rounded-none md:border-t-4 md:border-teal py-2 pl-4 md:pl-0 mt-2 space-y-2 md:space-y-0" data-id="element-254">
                  <li data-id="element-255">
                    <a href="/super-platform-ai" className="block px-4 py-3 text-navy hover:bg-gray-50 hover:text-teal transition-colors text-sm md:text-base" onClick={() => {
                  setIsProductsOpen(false);
                  setIsMenuOpen(false);
                }} data-id="element-256">
                      Super Platform AI
                    </a>
                  </li>
                  <li data-id="element-257">
                    <a href="/quasar" className="block px-4 py-3 text-navy hover:bg-gray-50 hover:text-teal transition-colors text-sm md:text-base" onClick={() => {
                  setIsProductsOpen(false);
                  setIsMenuOpen(false);
                }} data-id="element-258">
                      Quasar Platform
                    </a>
                  </li>
                  <li data-id="element-259">
                    <a href="/udsa" className="block px-4 py-3 text-navy hover:bg-gray-50 hover:text-teal transition-colors text-sm md:text-base" onClick={() => {
                  setIsProductsOpen(false);
                  setIsMenuOpen(false);
                }} data-id="element-260">
                      UDSA Platform
                    </a>
                  </li>
                </ul>}
            </li>
          </ul>
        </nav>
      </div>
    </header>;
}